import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Check, Link, Zap, Info, Users } from "lucide-react";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@/components/ui/tooltip";

interface StripePlan {
	product_id: string;
	product_name: string;
	description: string | null;
	price_id: string;
	currency: string;
	unit_amount: number;
	product_attrs: {
		id: string;
		name: string;
		type: string;
		active: boolean;
		description: string | null;
		default_price: string;
	};
	price_attrs: {
		id: string;
		type: string;
		active: boolean;
		currency: string;
		recurring: {
			interval: string;
			interval_count: number;
			usage_type: string;
		};
		lookup_key: string;
		unit_amount: number;
		unit_amount_decimal: string;
	};
}

interface PlanSelectionProps {
	availablePlans: StripePlan[];
	isLoading: boolean;
	selectedPriceId: string;
	discountCode: string;
	onPlanSelect: (productId: string, priceId: string) => void;
	onDiscountCodeChange: (code: string) => void;
	onValidate: () => void;
	isCreating: boolean;
}

export const PlanSelection = ({
	availablePlans,
	isLoading,
	selectedPriceId,
	discountCode,
	onPlanSelect,
	onDiscountCodeChange,
	onValidate,
	isCreating,
}: PlanSelectionProps) => {
	const { t } = useTranslation();

	// Group plans by product
	const groupedPlans = availablePlans.reduce((acc, plan) => {
		if (!acc[plan.product_id]) {
			acc[plan.product_id] = {
				product: plan.product_attrs,
				prices: []
			};
		}
		acc[plan.product_id].prices.push(plan);
		return acc;
	}, {} as Record<string, { product: any; prices: StripePlan[] }>);

	const formatPrice = (amount: number, currency: string) => {
		return new Intl.NumberFormat("en-US", {
			style: "currency",
			currency: currency.toUpperCase(),
		}).format(amount / 100);
	};
	if (isLoading) {
		return (
			<Card>
				<CardHeader>
					<CardTitle>{t("register.payment.loadingTitle", "Loading Plans...")}</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="flex items-center justify-center py-8">
						<div className="text-sm text-muted-foreground">{t("register.payment.loadingMessage", "Loading available plans...")}</div>
					</div>
				</CardContent>
			</Card>
		);
	}

	return (
		<div className="space-y-6">
			<Card>
				<CardHeader>
					<CardTitle>{t("register.payment.title", "Choose Your Plan")}</CardTitle>
					<CardDescription>
						{t("register.payment.description", "Select a plan that fits your organization's needs. See details on")} <a href="https://digger-ai.be/pricing" target="_blank" style={{ textDecoration: "underline" }}>{t("register.payment.pricingPageLink", "our pricing page")}</a>.
					</CardDescription>
					<div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-sm">
						<p className="text-xs text-gray-600 font-normal flex items-center gap-2">
							<Users className="h-3 w-3" />
							{t("register.payment.userCreationNote") || "Note: You're creating the first user (admin) for your organization. Additional users can be invited through the app after registration."}
						</p>
					</div>
				</CardHeader>
				<CardContent className="space-y-6">
					{Object.entries(groupedPlans).map(([productId, { product, prices }]) => {
						// Determine if this is Tier 1 (Standard) or Tier 2 (Premium)
						const isTier1 = product.name.toLowerCase().includes('tier 1') || product.name.toLowerCase().includes('standard');
						const isTier2 = product.name.toLowerCase().includes('tier 2') || product.name.toLowerCase().includes('premium');
						
						return (
							<div key={productId} className="space-y-4">
								<div className="flex items-center gap-2">
									<h3 className="text-lg font-semibold">{product.name}</h3>
									{(isTier1 || isTier2) && (
										<Tooltip>
											<TooltipTrigger asChild>
												<Button
													variant="ghost"
													size="sm"
													className="h-6 w-6 p-0 rounded-sm hover:bg-gray-100"
												>
													<Info className="h-4 w-4 text-gray-500" />
												</Button>
											</TooltipTrigger>
											<TooltipContent className="max-w-sm p-4">
												<div className="space-y-3">
													<h4 className="font-semibold text-sm">
														{isTier1 ? t("register.pricing.tooltip.standardPack") : t("register.pricing.tooltip.premiumPack")}
													</h4>
													<div className="text-xs space-y-2">
														{isTier1 ? (
															<>
																<p className="font-medium text-gray-700">{t("register.pricing.tooltip.forGrowingTeams")}</p>
																<ul className="space-y-1 text-gray-600">
																	<li>• {t("register.pricing.tooltip.aiPoweredMatching")}</li>
																	<li>• {t("register.pricing.tooltip.fullCrmAts")}</li>
																	<li>• {t("register.pricing.tooltip.upTo2000Cvs")}</li>
																	<li>• {t("register.pricing.tooltip.upTo50JobOffers")}</li>
																	<li>• {t("register.pricing.tooltip.calendarSync")}</li>
																	<li>• {t("register.pricing.tooltip.kanbanPipeline")}</li>
																	<li>• {t("register.pricing.tooltip.emailSupport")}</li>
																</ul>
															</>
														) : (
															<>
																<p className="font-medium text-gray-700">{t("register.pricing.tooltip.forEnterprises")}</p>
																<ul className="space-y-1 text-gray-600">
																	<li>• {t("register.pricing.tooltip.everythingInStandard")}</li>
																	<li>• {t("register.pricing.tooltip.upTo10000Cvs")}</li>
																	<li>• {t("register.pricing.tooltip.upTo100JobOffers")}</li>
																	<li>• {t("register.pricing.tooltip.fullSync")}</li>
																	<li>• {t("register.pricing.tooltip.prioritySupport")}</li>
																</ul>
															</>
														)}
													</div>
												</div>
											</TooltipContent>
										</Tooltip>
									)}
								</div>
								{product.description && (
									<p className="text-sm text-muted-foreground">{product.description}</p>
								)}
								
								<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
									{prices.map((plan) => (
										<Card 
											key={plan.price_id}
											className={`cursor-pointer transition-all ${
												selectedPriceId === plan.price_id 
													? "ring-2 ring-digger-blue border-digger-blue" 
													: "hover:border-gray-300"
											}`}
											onClick={() => onPlanSelect(plan.product_id, plan.price_id)}
										>
											<CardHeader className="pb-4">
												<div className="flex items-center justify-between">
													<div>
														<Badge variant={plan.price_attrs.recurring.interval === "year" ? "secondary" : "outline"}>
															{plan.price_attrs.recurring.interval === "year" ? t("register.payment.annual", "Annual") : t("register.payment.monthly", "Monthly")}
														</Badge>
														{plan.price_attrs.recurring.interval === "year" && (
															<Badge variant="default" className="ml-2 bg-green-100 text-green-800">
																{t("register.payment.save20", "Save 20%")}
															</Badge>
														)}
													</div>
													{selectedPriceId === plan.price_id && (
														<Check className="h-5 w-5 text-digger-blue" />
													)}
												</div>
											</CardHeader>
											<CardContent>
												<div className="space-y-2">
													<div className="text-2xl font-bold">
														{formatPrice(plan.unit_amount, plan.currency)}
														<span className="text-sm font-normal text-muted-foreground">
															/{plan.price_attrs.recurring.interval}
														</span>
													</div>												
													<p className="text-sm text-muted-foreground">
														{t("register.payment.billedFrequency", "Billed {{frequency}}", { frequency: plan.price_attrs.recurring.interval === "year" ? t("register.payment.annually", "annually") : t("register.payment.monthly", "monthly") })}
													</p>
												</div>
											</CardContent>
										</Card>
									))}
								</div>
							</div>
						);
					})}
					
					{/* Discount Code */}
					<div className="space-y-2">
						<Label htmlFor="discount-code">{t("register.payment.discountCode", "Discount Code (Optional)")}</Label>
						<Input
							id="discount-code"
							type="text"
							value={discountCode}
							onChange={(e) => onDiscountCodeChange(e.target.value)}
							placeholder={t("register.payment.discountCodePlaceholder", "Enter discount code")}
							className="max-w-md"
						/>
					</div>					{/* Validate Button */}
					<div className="flex justify-end pt-4">
						<Button
							onClick={onValidate}
							disabled={!selectedPriceId || isCreating}
							className="bg-digger-blue hover:bg-digger-blue-light"
						>
							{isCreating ? t("register.payment.savingPlan", "Saving Plan Selection ...") : t("register.payment.validatePlan", "Validate Plan")}
						</Button>
					</div>
									</CardContent>
				</Card>
			</div>
		);
};
